import {
  AccessorKeyColumnDef,
  Column,
  ColumnDef,
  ColumnFiltersState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  InitialTableState,
  SortingState,
  useReactTable,
} from '@tanstack/react-table';
import filter from 'lodash/filter';
import get from 'lodash/get';
import {
  CSSProperties,
  Fragment,
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDebounce } from 'use-debounce';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useGetTestId } from '@/hooks';
import { cn } from '@/lib/utils';

import { Pagination } from './pagination';
import { SearchField } from './search-field';

export function GenericTable<DataType, SubRowDataType>({
  data,
  onClick = () => {},
  columns,
  subColumns,
  subKey,
  onSubClick = () => {},
  disabledSubColumnsIdsToClick = [],
  children,
  searchable = false,
  searchPlaceholder,
  pinEnabled = false,
  name = 'table',
  tableRowClassName,
  emptyCellClassName,
  tableCellClassName,
  tableHeadCellClassName,
  tableFilterClassName,
  initialState = {},
  searchColumns = [],
  onRowSelectionChange,
  sizeStyleProperty = 'width',
  emptyDescription = 'No results.',
  emptyTitle,
  emptyButton,
  isFiltered = false,
  filteredKey,
  disabledColumnsIdsToClick = [],
  countable = false,
  countLabel = '',
  renderSubFooter,
  rowDraggable = false,
  getRowDragId,
  onRowDragEnd,
}: PropsWithChildren<{
  name?: string;
  data: DataType[];
  searchable?: boolean;
  pinEnabled?: boolean;
  columns: ColumnDef<DataType>[];
  subColumns?: AccessorKeyColumnDef<SubRowDataType>[];
  subKey?: string;
  onSubClick?: (item: SubRowDataType) => void;
  disabledSubColumnsIdsToClick?: string[];
  emptyDescription?: string;
  emptyCellClassName?: string;
  searchColumns?: string[];
  tableRowClassName?: string;
  searchPlaceholder?: string;
  tableCellClassName?: string;
  tableHeadCellClassName?: string;
  tableFilterClassName?: string;
  onClick?: (item: DataType) => void;
  sizeStyleProperty?: 'width' | 'minWidth';
  initialState?: InitialTableState;
  onRowSelectionChange?: (selection: string[]) => void;
  emptyTitle?: string;
  emptyButton?: ReactNode;
  isFiltered?: boolean;
  filteredKey?: string;
  disabledColumnsIdsToClick?: string[];
  countable?: boolean;
  countLabel?: string;
  renderSubFooter?: (rowOriginal: DataType) => ReactNode;
  rowDraggable?: boolean;
  getRowDragId?: (rowOriginal: DataType) => string;
  onRowDragEnd?: (draggedRow: DataType, targetRow: DataType) => void;
}>) {
  const testId = useGetTestId(name);
  const [rowSelection, setRowSelection] = useState({});
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [searchValue, setSearchValue] = useState('');
  const [draggedRow, setDraggedRow] = useState<DataType | null>(null);
  const [dragOverRowId, setDragOverRowId] = useState<string | null>(null);

  const [debouncedRowSelection] = useDebounce(rowSelection, 300);

  const filteredData = useMemo(() => {
    const lowerTerm = searchValue.toLowerCase();

    if (!searchValue || !searchColumns.length) return data;

    return filter(data, (item) =>
      searchColumns.some((key) => {
        const val = get(item, key);

        return typeof val === 'string' && val.toLowerCase().includes(lowerTerm);
      })
    );
  }, [data, searchValue]);

  const table = useReactTable({
    data: filteredData,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onRowSelectionChange: setRowSelection,
    getRowId: (originalRow) => (originalRow as { id: string }).id,
    state: {
      sorting,
      columnFilters,
      rowSelection,
    },
    defaultColumn: {
      size: Number.MAX_SAFE_INTEGER,
    },
    autoResetPageIndex: false,
    initialState: {
      pagination: {
        pageIndex: 0,
        pageSize: 15,
      },
      ...initialState,
    },
    columnResizeMode: 'onChange',
  });

  useEffect(() => {
    onRowSelectionChange?.(
      table.getSelectedRowModel().rows.map((row) => {
        const original = row.original as { id: string };

        return original.id;
      })
    );
  }, [Object.keys(debouncedRowSelection).length]);

  const isFilterEmpty =
    filteredKey &&
    table.getRowModel().rows.filter((row) => row.original[filteredKey])
      .length === 0;

  const getCommonPinningStyles = (column: Column<DataType>): CSSProperties => {
    if (!pinEnabled) return {};

    const isPinned = column.getIsPinned();
    const isLastLeftPinnedColumn =
      isPinned === 'left' && column.getIsLastColumn('left');
    const isFirstRightPinnedColumn =
      isPinned === 'right' && column.getIsFirstColumn('right');

    return {
      boxShadow: isLastLeftPinnedColumn
        ? '-2px 0 2px -2px gray inset'
        : isFirstRightPinnedColumn
          ? '2px 0 2px -2px gray inset'
          : undefined,
      left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
      right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
      position: isPinned ? 'sticky' : 'relative',
      width: column.getSize(),
      zIndex: isPinned ? 1 : 0,
    };
  };

  return (
    <div className="w-full">
      <div
        className={cn(
          'flex justify-between items-center gap-4 mb-5',
          tableFilterClassName
        )}
      >
        {searchable && (
          <SearchField
            placeholder={searchPlaceholder}
            defaultValue={searchValue}
            className="w-full max-w-[400px]"
            onChange={(value) => setSearchValue(value)}
          />
        )}
        {children}
      </div>

      {countable && (
        <div className="text-zinc-400 text-sm mb-5">
          {countLabel} {data.length}
        </div>
      )}

      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id} className="bg-zinc-900 border-0">
              {headerGroup.headers.map((header) => {
                const size = header.getSize();

                return (
                  <TableHead
                    key={header.id}
                    style={{
                      [sizeStyleProperty]:
                        size === Number.MAX_SAFE_INTEGER
                          ? undefined
                          : `${header.getSize()}px`,
                      ...getCommonPinningStyles(header.column),
                    }}
                    className={cn('bg-zinc-900', tableHeadCellClassName)}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>

        <TableBody>
          {table.getRowModel().rows?.length && !isFilterEmpty ? (
            table.getRowModel().rows.map((row, index) => {
              if (isFiltered && !row.original[filteredKey]) return null;
              const rowDragId = getRowDragId?.(row.original) ?? row.id;
              return (
                <Fragment key={`${row.id}-${index}`}>
                  <TableRow
                    data-testid={`${testId}-row-${index}`}
                    draggable={rowDraggable}
                    onDragStart={(event) => {
                      if (!rowDraggable) {
                        return;
                      }

                      setDraggedRow(row.original);
                      event.dataTransfer.effectAllowed = 'move';
                      event.dataTransfer.setData('text/plain', rowDragId);
                    }}
                    onDragOver={(event) => {
                      if (!rowDraggable || !draggedRow) {
                        return;
                      }

                      event.preventDefault();
                      event.dataTransfer.dropEffect = 'move';
                      setDragOverRowId(rowDragId);
                    }}
                    onDragLeave={() => {
                      if (dragOverRowId === rowDragId) {
                        setDragOverRowId(null);
                      }
                    }}
                    onDrop={(event) => {
                      if (!rowDraggable || !draggedRow) {
                        return;
                      }

                      event.preventDefault();
                      const draggedRowId = getRowDragId?.(draggedRow) ?? event.dataTransfer.getData('text/plain');
                      if (draggedRowId !== rowDragId) {
                        onRowDragEnd?.(draggedRow, row.original);
                      }

                      setDraggedRow(null);
                      setDragOverRowId(null);
                    }}
                    onDragEnd={() => {
                      setDraggedRow(null);
                      setDragOverRowId(null);
                    }}
                    className={cn(
                      Boolean(onClick) && 'cursor-pointer',
                      rowDraggable && 'cursor-grab active:cursor-grabbing',
                      dragOverRowId === rowDragId && 'outline outline-1 outline-primary',
                      tableRowClassName
                    )}
                  >
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <TableCell
                          key={cell.id}
                          className={cn(
                            'capitalize bg-zinc-950',
                            cell.column.id === 'status' && 'p-0',
                            tableCellClassName
                          )}
                          style={{ ...getCommonPinningStyles(cell.column) }}
                          onClick={() =>
                            // Disable click on some rows
                            disabledColumnsIdsToClick.includes(cell.column.id)
                              ? undefined
                              : onClick?.(row.original)
                          }
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>

                  {row.getIsExpanded() && (
                    <>
                      {(() => {
                        const subData = row.original[
                          subKey
                        ] as SubRowDataType[];

                        if (
                          !(Array.isArray(subData) && subData.length > 0) ||
                          !subColumns
                        ) {
                          return (
                            <>
                              <TableRow>
                                <TableCell colSpan={columns.length}>
                                  <div className="text-zinc-500 italic">
                                    No additional details.
                                  </div>
                                </TableCell>
                              </TableRow>
                              {renderSubFooter && (
                                <TableRow>
                                  <TableCell
                                    colSpan={columns.length}
                                    className="p-0"
                                  >
                                    {renderSubFooter(row.original)}
                                  </TableCell>
                                </TableRow>
                              )}
                            </>
                          );
                        }

                        return (
                          <>
                            {subData.map((sub, index) => (
                              <TableRow key={`sub-${index}`}>
                                {subColumns.map((column) => {
                                  const key =
                                    column.accessorKey as keyof SubRowDataType;
                                  const value = key ? sub[key] : undefined;

                                  return (
                                    <TableCell
                                      key={String(key)}
                                      className={tableCellClassName}
                                      style={{ width: column?.size }}
                                      onClick={() => {
                                        if (
                                          disabledSubColumnsIdsToClick.includes(
                                            column.id ||
                                              (column.accessorKey as string)
                                          )
                                        ) {
                                          return;
                                        }

                                        onSubClick({
                                          ...sub,
                                          parent: row.original,
                                        });
                                      }}
                                    >
                                      {column.cell
                                        ? flexRender(column.cell, {
                                            getValue: () => value,
                                            row: {
                                              original: {
                                                ...sub,
                                                parent: row.original,
                                              },
                                            },
                                          } as any)
                                        : String(value ?? '')}
                                    </TableCell>
                                  );
                                })}
                              </TableRow>
                            ))}
                            {renderSubFooter && (
                              <TableRow>
                                <TableCell
                                  colSpan={columns.length}
                                  className="p-0"
                                >
                                  {renderSubFooter(row.original)}
                                </TableCell>
                              </TableRow>
                            )}
                          </>
                        );
                      })()}
                    </>
                  )}
                </Fragment>
              );
            })
          ) : (
            <TableRow className={tableRowClassName} data-testid="empty-row">
              {searchValue || isFiltered ? (
                <TableCell
                  colSpan={columns.length}
                  className={cn(
                    'h-80 text-center text-zinc-500',
                    emptyCellClassName
                  )}
                >
                  No results found
                </TableCell>
              ) : (
                <TableCell
                  colSpan={columns.length}
                  className={cn('h-80', emptyCellClassName)}
                >
                  <div
                    className={cn(
                      'flex flex-col gap-2 justify-start items-start',
                      {
                        'items-center': !emptyTitle,
                      }
                    )}
                  >
                    <p className="text-md text-semibold">
                      {emptyTitle && emptyTitle}
                      <p
                        className={cn('text-zinc-500 text-xs', {
                          'text-md': !emptyTitle,
                        })}
                      >
                        {emptyDescription}
                      </p>
                    </p>
                    {emptyButton && emptyButton}
                  </div>
                </TableCell>
              )}
            </TableRow>
          )}
        </TableBody>
      </Table>

      <Pagination
        className="-ml-[24px] -mr-[24px]"
        table={table}
        totalCount={undefined}
      />
    </div>
  );
}
